import React, { createContext, useContext, useEffect, useState } from "react";

/* ------------------------------------------------------------------
   DEFAULT ANALYTICS STATE
------------------------------------------------------------------ */
const defaultAnalytics = {
  attempts: [],
  totalAttempts: 0,
  totalPassed: 0,
  averageScore: 0,
  completedModules: 0,
  totalTimeSpent: 0,
  modulePerformance: {},
  streakDays: 0,
  lastActivityDate: "",
};

const STORAGE_KEY = "quiz_analytics_data";

const QuizAnalyticsContext = createContext(null);

/* ------------------------------------------------------------------
   PROVIDER
------------------------------------------------------------------ */
export const QuizAnalyticsProvider = ({ children }) => {
  const [analytics, setAnalytics] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : defaultAnalytics;
    } catch {
      return defaultAnalytics;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(analytics));
  }, [analytics]);

  const recordAttempt = (attempt) => {
    const newAttempt = {
      ...attempt,
      timestamp: new Date().toISOString(),
    };

    setAnalytics((prev) => {
      const attempts = [...prev.attempts, newAttempt];
      const modulePerf = { ...prev.modulePerformance };

      const existing = modulePerf[attempt.moduleId];

      if (existing) {
        modulePerf[attempt.moduleId] = {
          ...existing,
          attempts: existing.attempts + 1,
          bestScore: Math.max(existing.bestScore, attempt.score),
          averageScore: Math.round(
            (existing.averageScore * existing.attempts + attempt.score) /
              (existing.attempts + 1)
          ),
          passed: existing.passed || attempt.passed,
          lastAttempt: newAttempt.timestamp,
        };
      } else {
        modulePerf[attempt.moduleId] = {
          moduleId: attempt.moduleId,
          moduleName: attempt.moduleName,
          attempts: 1,
          bestScore: attempt.score,
          averageScore: attempt.score,
          passed: attempt.passed,
          lastAttempt: newAttempt.timestamp,
        };
      }

      const totalPassed = Object.values(modulePerf).filter(
        (m) => m.passed
      ).length;

      const averageScore = Math.round(
        attempts.reduce((sum, a) => sum + a.score, 0) / attempts.length
      );

      const totalTimeSpent = attempts.reduce(
        (sum, a) => sum + a.timeSpentSeconds,
        0
      );

      return {
        attempts,
        totalAttempts: attempts.length,
        totalPassed,
        averageScore,
        completedModules: totalPassed,
        totalTimeSpent,
        modulePerformance: modulePerf,
        streakDays: prev.streakDays,
        lastActivityDate: newAttempt.timestamp,
      };
    });
  };

  const resetAnalytics = () => {
    setAnalytics(defaultAnalytics);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <QuizAnalyticsContext.Provider
      value={{ analytics, recordAttempt, resetAnalytics }}
    >
      {children}
    </QuizAnalyticsContext.Provider>
  );
};

/* ------------------------------------------------------------------
   HOOK
------------------------------------------------------------------ */
export const useQuizAnalytics = () => {
  const context = useContext(QuizAnalyticsContext);
  if (!context) {
    throw new Error(
      "useQuizAnalytics must be used within QuizAnalyticsProvider"
    );
  }
  return context;
};
